import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyPopup } from '../../interfaces/Popup/Popup';
import {
  createPopup,
  deletePopup,
  getPopup,
  updatePopup,
  updatePopupMedia,
} from '../../api/Popup';
import { MediaOptions } from '../../interfaces/MediaOptions';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import NumberInput from '../../components/NumberInput/NumberInput';
import { ImageInput } from '../../components/ImageInput/ImageInput';
import { getFeURL } from '../../api/Settings';
import { ProductSlugsDropdown } from '../../components/Dropdowns/ProductSlugsDropdown';
import { CategorySlugsDropdown } from '../../components/Dropdowns/CategorySlugsDropdown';
import { BrandSlugsDropdown } from '../../components/Dropdowns/BrandSlugsDropdown';
import { LanguageDropdown } from '../../components/Dropdowns/LanguageDropdown';

export const ModifyPopupPage = () => {
  const navigate = useNavigate();

  const { popupID: popupIDAsStr } = useParams<string>();

  const popupID = popupIDAsStr ? Number(popupIDAsStr) : undefined;
  const isEditing = Number.isFinite(popupID);

  const [popup, setPopup] = useState(getEmptyPopup());
  const [feURL, setFeURL] = useState('');
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshPopup = () => {
    setLoading(true);
    getPopup(popupID!)
      .then((p) => setPopup(p))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    getFeURL()
      .then((url) => setFeURL(url))
      .catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    if (isEditing) {
      refreshPopup();
    }
  }, [popupIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updatePopup(popup)
        .then((updatedPopup) => {
          toast.success('Pop-up başarıyla güncellendi');
          setPopup(updatedPopup);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createPopup(popup)
        .then((createdPopup) => {
          toast.success('Pop-up başarıyla oluşturuldu');
          navigate(`/visual/popups/${createdPopup.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleUpdateMedia = (media: MediaOptions) => {
    setLoading(true);
    updatePopupMedia(popup.id, media)
      .then(() => {
        toast.success('Medya başarıyla güncellendi');
        refreshPopup();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deletePopup(popup.id)
      .then(() => {
        toast.success('Pop-up başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/visual/popups');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content="Gerçekten seçili pop-up'ı silmek istiyor musunuz"
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? "Pop-up'ı Düzenle" : 'Yeni Pop-up'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: 448,
            height: 'fit-content',
            p: 3,
            boxSizing: 'border-box',
            gap: 1.5,
          }}
        >
          <TextField
            label='Başlık'
            variant='outlined'
            size='small'
            value={popup.title}
            onChange={(e) => setPopup({ ...popup, title: e.target.value })}
            fullWidth
          />
          <TextField
            label='Link'
            variant='outlined'
            size='small'
            value={popup.linkUrl}
            onChange={(e) => setPopup({ ...popup, linkUrl: e.target.value })}
            fullWidth
            helperText={
              !!popup.linkUrl && !!feURL
                ? `${feURL}${popup.linkUrl}`
                : undefined
            }
          />
          <NumberInput
            label='Kullanıcı başı gösterim sınırı'
            placeholder='Yok'
            value={popup.maxShowPerUser}
            onChange={(x) =>
              setPopup({
                ...popup,
                maxShowPerUser: !!x && Number.isFinite(x) ? x : undefined,
              })
            }
          />
          <NumberInput
            label='Gecikme süresi'
            value={popup.delaySeconds}
            onChange={(x) => setPopup({ ...popup, delaySeconds: x })}
            endAdornment='Saniye'
          />

          <LanguageDropdown
            label='Hedef Dil'
            allowDefault
            defaultOptionLabel='Tüm Diller'
            value={popup.locale || null}
            onChange={(x) => setPopup({ ...popup, locale: x || undefined })}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={popup.showInAllProducts}
                onChange={() =>
                  setPopup({
                    ...popup,
                    showInAllProducts: !popup.showInAllProducts,
                    relations: { ...popup.relations, productSlugs: [] },
                  })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>
                Tüm ürünlerde göster
              </Typography>
            }
          />

          <ProductSlugsDropdown
            label='Ürünler'
            productSlugs={popup.relations.productSlugs}
            disabled={popup.showInAllProducts}
            onChange={(x) =>
              setPopup({
                ...popup,
                showInAllProducts: false,
                relations: { ...popup.relations, productSlugs: x },
              })
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={popup.showInAllCategories}
                onChange={() =>
                  setPopup({
                    ...popup,
                    showInAllCategories: !popup.showInAllCategories,
                    relations: { ...popup.relations, categorySlugs: [] },
                  })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>
                Tüm Kategorilerde göster
              </Typography>
            }
          />

          <CategorySlugsDropdown
            label='Kategoriler'
            categorySlugs={popup.relations.categorySlugs}
            disabled={popup.showInAllCategories}
            onChange={(x) =>
              setPopup({
                ...popup,
                showInAllCategories: false,
                relations: { ...popup.relations, categorySlugs: x },
              })
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={popup.showInAllBrands}
                onChange={() =>
                  setPopup({
                    ...popup,
                    showInAllBrands: !popup.showInAllBrands,
                    relations: { ...popup.relations, brandSlugs: [] },
                  })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>
                Tüm markalarda göster
              </Typography>
            }
          />

          <BrandSlugsDropdown
            label='Markalar'
            brandSlugs={popup.relations.brandSlugs}
            disabled={popup.showInAllBrands}
            onChange={(x) =>
              setPopup({
                ...popup,
                showInAllBrands: false,
                relations: { ...popup.relations, brandSlugs: x },
              })
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={popup.showInHome}
                onChange={() =>
                  setPopup({ ...popup, showInHome: !popup.showInHome })
                }
              />
            }
            label={
              <Typography sx={sharedStyles.subtitle2}>
                Ana sayfada göster
              </Typography>
            }
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={popup.isActive}
                  onChange={() =>
                    setPopup({ ...popup, isActive: !popup.isActive })
                  }
                />
              }
              label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              onClick={handleSave}
              sx={{ ...sharedStyles.buttonText }}
            >
              Kaydet
            </Button>
          </Box>
        </Paper>

        {isEditing && (
          <ImageInput
            title='Resim'
            existingImage={popup.image}
            onUpload={(file) => handleUpdateMedia({ image: file })}
            onDelete={() => handleUpdateMedia({ eraseImage: true })}
          />
        )}
      </Box>
    </Box>
  );
};
